import type { Tables } from '@auctisync/shared';

/**
 * The columns a page may `select` on tables that hide a column from the
 * shared `anon` / `authenticated` roles.
 *
 * `20260923250000_staff_ids_off_public_rows.sql` took the staff account id
 * off the public grant of `auctions`, `inventory_photos` and
 * `auction_preview_events` (`created_by` / `uploaded_by`). A column grant
 * means `select('*')` fails for EVERY caller, staff included — Postgres
 * expands `*` to the hidden column and refuses the whole read — so these
 * pages name their columns instead. Same shape as `PUBLIC_LOT_COLUMNS`.
 *
 * ONE string literal each, deliberately: the typed client infers the row
 * shape from the literal; a concatenated string degrades it to
 * GenericStringError. The type-level checks at the bottom fail the build if
 * a list and its row type ever disagree — a column added to the table must
 * be added here (and granted, in its migration) to be read.
 */

// `20261007140000_engine_bid_path_switch.sql` added the sale's book — the
// operator's override (`bid_path`) and when the engine took the sale
// (`engine_published_at`). Granted to neither shared role: a client learns a
// lot's book from `get_bid_path`, and the operator reads the switch through
// `get_engine_switch`.
export type AuctionRow = Omit<
  Tables<'auctions'>,
  'created_by' | 'bid_path' | 'engine_published_at'
>;
export const AUCTION_ROW_COLUMNS =
  'id, tenant_id, slug, reference_number, title, description, preview_text, auction_type, category_id, starts_at, ends_at, hide_starts_at, hide_ends_at, preview_starts_at, preview_ends_at, stagger_qty, stagger_seconds, extension_threshold_seconds, extension_amount_seconds, extension_method, default_starting_bid_cents, default_bid_increment_scale_id, lock_bid_amount, requires_bidder_approval, requires_terms_acceptance, requires_cc_preauth, cc_preauth_amount_cents, hide_winning_info, hide_bid_history, hide_winning_user, buyers_premium_bps, online_premium_bps, pickup_location, pickup_address_line1, pickup_address_line2, pickup_city, pickup_region, pickup_postal_code, pickup_country, pickup_dates, pickup_instructions, status, is_featured, is_visible, image_tag, image_tag_color, link_url, link_text, alert_color, alert_text, custom_fields, lot_count, total_bid_count, total_hammer_cents, outage_extended_at, outage_extended_by_seconds, outage_extension_reason, created_at, updated_at, photo_file_prefix, ending_soon_notified_at, consignment_profile_id, tax_profile_id, allow_preview_bidding, hide_unsold_lots, timezone';

// `20261001120000_photo_edits.sql` added six staff-only columns — a photo's
// edit and where its original, render and cut-out live. Granted to neither
// shared role, so they are not on this row; staff read them through
// `photo_edits_staff` (`@auctisync/api-client` fetchStaffLotPhotos).
export type InventoryPhotoRow = Omit<
  Tables<'inventory_photos'>,
  | 'uploaded_by'
  | 'edit'
  | 'original_url'
  | 'edited_r2_key'
  | 'mask_r2_key'
  | 'mask_url'
  | 'edited_at'
>;
export const INVENTORY_PHOTO_ROW_COLUMNS =
  'id, tenant_id, inventory_item_id, storage_path, display_url, sort_order, caption, width, height, size_bytes, mime_type, created_at, r2_key, width_px, height_px, ai_alt_text, processing_status, display_filename, image_sequence, lot_id, client_upload_id';

export type PreviewEventRow = Omit<Tables<'auction_preview_events'>, 'created_by'>;
export const PREVIEW_EVENT_ROW_COLUMNS =
  'id, auction_id, title, location, starts_at, ends_at, notes, created_at, updated_at';

// ─── The lists and the row types must agree, both ways ─────────────────────

// Tail-recursive (an accumulator), so a 60-column list stays inside the
// compiler's instantiation depth.
type Split<S extends string, Acc extends string = never> = S extends `${infer Head}, ${infer Tail}`
  ? Split<Tail, Acc | Head>
  : Acc | S;
type SameKeys<Listed extends string, Row> = [Exclude<keyof Row, Listed>] extends [never]
  ? [Exclude<Listed, keyof Row>] extends [never]
    ? true
    : never
  : never;

const auctionListMatchesRow: SameKeys<Split<typeof AUCTION_ROW_COLUMNS>, AuctionRow> = true;
const photoListMatchesRow: SameKeys<
  Split<typeof INVENTORY_PHOTO_ROW_COLUMNS>,
  InventoryPhotoRow
> = true;
const previewEventListMatchesRow: SameKeys<
  Split<typeof PREVIEW_EVENT_ROW_COLUMNS>,
  PreviewEventRow
> = true;
void auctionListMatchesRow;
void photoListMatchesRow;
void previewEventListMatchesRow;
